import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  DeskChatLiveEvent,
  DeskChatMessage,
  DeskChatService,
  DeskChatThread,
} from '../../../service/admin/desk-chat.service';
import { NotificationSoundService } from '../../../service/admin/notification-sound.service';
import { ToasterService } from '../../../service/common/toaster.service';

interface QuickLine {
  text: string;
  aboutPatient: boolean;
}

@Component({
  selector: 'app-desk-chat-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './desk-chat-panel.component.html',
  styleUrl: './desk-chat-panel.component.scss',
})
export class DeskChatPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;

  threads: DeskChatThread[] = [];
  messages: DeskChatMessage[] = [];
  activeClinicId: number | null = null;
  draft = '';
  includePatient = false;
  loadingMessages = false;
  sending = false;
  role = '';
  unreadTotal = 0;

  private readonly doctorQuickLines: QuickLine[] = [
    { text: 'Send the next patient in', aboutPatient: false },
    { text: 'This patient has left', aboutPatient: true },
    { text: 'Call this patient', aboutPatient: true },
    { text: 'Fees still pending', aboutPatient: true },
    { text: 'Hold the medicine', aboutPatient: false },
  ];

  private readonly receptionQuickLines: QuickLine[] = [
    { text: 'Next patient is ready', aboutPatient: false },
    { text: 'This patient is waiting', aboutPatient: true },
    { text: 'Patient has arrived', aboutPatient: true },
    { text: 'Fees collected', aboutPatient: true },
    { text: 'Medicine given', aboutPatient: true },
  ];

  private liveSub: Subscription | null = null;
  private userRole = '';

  constructor(
    private deskChat: DeskChatService,
    private sound: NotificationSoundService,
    private toaster: ToasterService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.userRole = this.readUserRole();
    this.refreshThreads();
    this.liveSub = this.deskChat.watch().subscribe((event) => this.onLiveMessage(event));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (!openChange || openChange.previousValue === openChange.currentValue || !this.open) {
      return;
    }
    this.sound.unlock();
    this.refreshThreads(true);
    if (this.activeClinicId) {
      this.loadMessages(this.activeClinicId);
    }
  }

  ngOnDestroy(): void {
    this.liveSub?.unsubscribe();
    this.liveSub = null;
  }

  get attachableName(): string {
    return this.deskChat.getAttachablePatient()?.full_name || '';
  }

  get quickLines(): QuickLine[] {
    return this.isReception ? this.receptionQuickLines : this.doctorQuickLines;
  }

  private get isReception(): boolean {
    return (this.userRole || this.role) === 'reception';
  }

  get otherUnread(): number {
    return this.threads
      .filter((thread) => thread.id !== this.activeClinicId)
      .reduce((sum, thread) => sum + (Number(thread.unread_count) || 0), 0);
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.sound.unlock();
    this.openChange.emit(!this.open);
  }

  selectThread(thread: DeskChatThread, event?: Event): void {
    event?.stopPropagation();
    this.activeClinicId = thread.id;
    this.loadMessages(thread.id);
  }

  backToThreads(event: Event): void {
    event.stopPropagation();
    this.activeClinicId = null;
    this.messages = [];
  }

  send(event?: Event): void {
    event?.stopPropagation();
    const body = this.draft.trim();
    if (!body || !this.activeClinicId || this.sending) {
      return;
    }
    this.dispatch(body, this.includePatient);
  }

  sendQuick(line: QuickLine, event: Event): void {
    event.stopPropagation();
    if (!this.activeClinicId || this.sending) {
      return;
    }
    this.dispatch(line.text, line.aboutPatient && !!this.attachableName);
  }

  openAttachedPatient(message: DeskChatMessage, event: Event): void {
    event.stopPropagation();
    const patientId = Number(message.patient?.id || 0);
    if (!patientId) {
      return;
    }
    const path = this.userRole === 'reception' ? '/admin/appointment' : '/admin/prescriptions';
    this.openChange.emit(false);
    this.router.navigate([path]).then(() => {
      this.deskChat.requestOpenPatient(patientId);
    });
  }

  onDraftKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  formatTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  }

  activeThreadName(): string {
    return this.threads.find((thread) => thread.id === this.activeClinicId)?.name || 'Clinic';
  }

  threadInitial(name: string): string {
    const word = String(name || '').trim().split(/\s+/)[0] || 'C';
    return word.slice(0, 1).toUpperCase();
  }

  toggleIncludePatient(event: Event): void {
    event.stopPropagation();
    this.includePatient = !this.includePatient;
  }

  private dispatch(body: string, attach: boolean): void {
    const clinicId = this.activeClinicId;
    if (!clinicId) {
      return;
    }
    const patientId = attach ? this.deskChat.getAttachablePatient()?.id : null;
    this.sending = true;
    this.sound.unlock();
    this.deskChat.sendMessage(clinicId, body, patientId).subscribe({
      next: (res: any) => {
        this.sending = false;
        this.draft = '';
        this.includePatient = false;
        if (res?.data) {
          this.messages = [...this.messages, res.data];
          this.scrollToEnd();
        }
        this.refreshThreads();
      },
      error: () => {
        this.sending = false;
      },
    });
  }

  private onLiveMessage(event: DeskChatLiveEvent): void {
    if (event.mine) {
      return;
    }
    const clinicId = Number(event.clinic_id);
    const watching = this.open && this.activeClinicId === clinicId;
    void this.sound.play(this.userRole || this.role, 'desk_chat');
    if (!watching) {
      const clinic = event.clinic_name || this.threads.find((thread) => thread.id === clinicId)?.name || 'Clinic';
      const who = event.sender_name || 'Desk';
      this.toaster.info(event.body, `${clinic} · ${who}`);
    }
    this.refreshThreads();
    if (watching) {
      this.loadMessages(clinicId);
    }
  }

  private refreshThreads(openSingleThread = false): void {
    this.deskChat.getThreads().subscribe({
      next: (res: any) => {
        const data = res?.data;
        this.role = data?.role || this.userRole;
        this.threads = Array.isArray(data?.threads) ? data.threads : [];
        this.unreadTotal = this.threads.reduce((sum, thread) => sum + (Number(thread.unread_count) || 0), 0);

        if (openSingleThread && this.threads.length === 1 && this.activeClinicId == null) {
          this.activeClinicId = this.threads[0].id;
          this.loadMessages(this.threads[0].id);
        }
      },
      error: () => undefined,
    });
  }

  private loadMessages(clinicId: number): void {
    this.loadingMessages = true;
    this.deskChat.getMessages(clinicId).subscribe({
      next: (res: any) => {
        this.loadingMessages = false;
        this.messages = Array.isArray(res?.data) ? res.data : [];
        this.scrollToEnd();
        this.deskChat.markRead(clinicId).subscribe({
          next: () => this.refreshThreads(),
        });
      },
      error: () => {
        this.loadingMessages = false;
        this.messages = [];
      },
    });
  }

  private scrollToEnd(): void {
    setTimeout(() => {
      const el = this.messageList?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }

  private readUserRole(): string {
    try {
      const user = JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
      return String(user?.type || '').toLowerCase();
    } catch {
      return '';
    }
  }
}
